import { useMemo } from 'react';
import { useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { KNOWLEDGE_BASES_QUERY_KEYS } from '@/entities/knowledge-base';
import { getKnowledgeBaseSummaryById } from '@/features/knowledge-bases';

/**
 * Hook for fetching a single knowledge base summary by ID
 * Focused responsibility: Single knowledge base summary data fetching
 */
export const useKnowledgeBaseDetail = () => {
  const { knowledgeBaseId = null } = useParams<{ knowledgeBaseId: string }>();

  const knowledgeBaseQuery = useQuery({
    queryKey: KNOWLEDGE_BASES_QUERY_KEYS.detail(knowledgeBaseId),
    queryFn: async () => {
      if (!knowledgeBaseId) {
        throw new Error('Knowledge base ID is required');
      }
      return await getKnowledgeBaseSummaryById(knowledgeBaseId);
    },
    enabled: !!knowledgeBaseId,
    retry: false,
    retryOnMount: false,
  });

  const isKnowledgeBaseDetail = useMemo(() => !!knowledgeBaseId, [knowledgeBaseId]);

  return {
    knowledgeBaseId,
    isKnowledgeBaseDetail,
    knowledgeBase: knowledgeBaseQuery.data,
    isLoading: knowledgeBaseQuery.isLoading,
    isFetching: knowledgeBaseQuery.isFetching,
    error: knowledgeBaseQuery.error,
    isError: knowledgeBaseQuery.isError,
    isSuccess: knowledgeBaseQuery.isSuccess,
    refetch: knowledgeBaseQuery.refetch,
  };
};
